import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Student,
  StudentReportItem,
  Teacher,
  TeacherReportItem,
  GroupReportItem,
} from '@/school-management/types/response';
import { studentService, teacherService, classService } from '@/school-management/services';
import { authActions } from './auth';

interface EntityState<T> {
  items: T[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  organizationId: string | null;
}

export interface EntitiesState {
  students: EntityState<StudentReportItem>;
  teachers: EntityState<TeacherReportItem>;
  classes: EntityState<GroupReportItem>;
}

const initialEntityState = <T>(): EntityState<T> => ({
  items: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  organizationId: null,
});

const initialState: EntitiesState = {
  students: initialEntityState<StudentReportItem>(),
  teachers: initialEntityState<TeacherReportItem>(),
  classes: initialEntityState<GroupReportItem>(),
};

// Async thunks for fetching
export const fetchStudents = createAsyncThunk(
  'entities/fetchStudents',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await studentService.getAllStudents(organizationId);
      return { students: response.data.items, organizationId };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch students');
    }
  }
);

export const fetchTeachers = createAsyncThunk(
  'entities/fetchTeachers',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await teacherService.getAllTeachers(organizationId);
      const teachers = response.data?.items ?? [];
      return { teachers, organizationId };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch teachers');
    }
  }
);

export const fetchClasses = createAsyncThunk(
  'entities/fetchClasses',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await classService.getAllClasses(organizationId);
      const classes = response.data?.items ?? [];
      return { classes, organizationId };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch classes');
    }
  }
);

const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    invalidateStudents: state => {
      state.students.lastFetched = null;
    },
    invalidateTeachers: state => {
      state.teachers.lastFetched = null;
    },
    invalidateClasses: state => {
      state.classes.lastFetched = null;
    },
    invalidateAll: state => {
      state.students.lastFetched = null;
      state.teachers.lastFetched = null;
      state.classes.lastFetched = null;
    },
    removeStudent: (state, action: PayloadAction<string>) => {
      state.students.items = state.students.items.filter(
        student => student.userId !== action.payload
      );
    },
    removeTeacher: (state, action: PayloadAction<string>) => {
      state.teachers.items = state.teachers.items.filter(
        teacher => teacher.userId !== action.payload
      );
    },
    removeClass: (state, action: PayloadAction<string>) => {
      state.classes.items = state.classes.items.filter(
        classItem => classItem.groupId !== action.payload
      );
    },
    clearAll: () => initialState,
  },
  extraReducers: builder => {
    // Clear entities cache when user signs out
    builder.addCase(authActions.signOut, () => initialState);
    // Students
    builder
      .addCase(fetchStudents.pending, state => {
        state.students.isLoading = true;
        state.students.error = null;
      })
      .addCase(
        fetchStudents.fulfilled,
        (
          state,
          action: PayloadAction<{ students: StudentReportItem[]; organizationId: string }>
        ) => {
          state.students.items = action.payload.students;
          state.students.isLoading = false;
          state.students.error = null;
          state.students.lastFetched = Date.now();
          state.students.organizationId = action.payload.organizationId;
        }
      )
      .addCase(fetchStudents.rejected, (state, action) => {
        state.students.isLoading = false;
        state.students.error = (action.payload as string) || 'Failed to fetch students';
      });

    // Teachers
    builder
      .addCase(fetchTeachers.pending, state => {
        state.teachers.isLoading = true;
        state.teachers.error = null;
      })
      .addCase(
        fetchTeachers.fulfilled,
        (
          state,
          action: PayloadAction<{ teachers: TeacherReportItem[]; organizationId: string }>
        ) => {
          state.teachers.items = action.payload.teachers;
          state.teachers.isLoading = false;
          state.teachers.error = null;
          state.teachers.lastFetched = Date.now();
          state.teachers.organizationId = action.payload.organizationId;
        }
      )
      .addCase(fetchTeachers.rejected, (state, action) => {
        state.teachers.isLoading = false;
        state.teachers.error = (action.payload as string) || 'Failed to fetch teachers';
      });

    // Classes
    builder
      .addCase(fetchClasses.pending, state => {
        state.classes.isLoading = true;
        state.classes.error = null;
      })
      .addCase(
        fetchClasses.fulfilled,
        (state, action: PayloadAction<{ classes: GroupReportItem[]; organizationId: string }>) => {
          state.classes.items = action.payload.classes;
          state.classes.isLoading = false;
          state.classes.error = null;
          state.classes.lastFetched = Date.now();
          state.classes.organizationId = action.payload.organizationId;
        }
      )
      .addCase(fetchClasses.rejected, (state, action) => {
        state.classes.isLoading = false;
        state.classes.error = (action.payload as string) || 'Failed to fetch classes';
      });
  },
});

export const entitiesActions = entitiesSlice.actions;
export const entitiesSelectors = {
  students: (state: { entities: EntitiesState }) => state.entities.students,
  teachers: (state: { entities: EntitiesState }) => state.entities.teachers,
  classes: (state: { entities: EntitiesState }) => state.entities.classes,
};

export default entitiesSlice.reducer;
